import React, { useCallback, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Alert, Button, Chip, Stack, Typography } from '@mui/material'
import { playerRequest, retryAnalysis } from '../../service'
import { apiErrorText, clipCoachingState, clipJobFor } from '../../service/coaching'

// D20: on the clip itself, show whether coaching is queued, running, ready or failed, open the
// result from here, and never offer a second request while one is queued or running.
export default function ClipCoachingStatus ({ uploadId, highlight, refreshKey, onRequest, onView }) {
  const [job, setJob] = useState(null)
  const [loaded, setLoaded] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [reload, setReload] = useState(0)
  const start = highlight?.start
  const end = highlight?.end
  useEffect(() => { setJob(null); setLoaded(false); setError('') }, [uploadId, highlight?.id])
  useEffect(() => {
    let live = true
    let timer
    const refresh = async () => {
      try {
        const status = await playerRequest(`/uploads/${uploadId}/analysis`)
        if (!live) return
        const latest = clipJobFor(status.jobs || [], { start, end })
        setJob(latest); setLoaded(true)
        // Poll only while this clip's run is queued or running.
        if (['queued', 'running'].includes(clipCoachingState(latest).state)) timer = setTimeout(refresh, 5000)
      } catch (_) {
        if (live) { setLoaded(true); setError('Coaching status could not be loaded. Retry in a moment.') }
      }
    }
    refresh()
    return () => { live = false; clearTimeout(timer) }
  }, [uploadId, start, end, refreshKey, reload])
  const status = clipCoachingState(job)
  const view = useCallback(async () => {
    setBusy(true); setError('')
    try { onView(await playerRequest(`/uploads/${uploadId}/analysis/${job.id}`)) } catch (e) { setError(apiErrorText(e, 'The coaching could not be opened. Retry in a moment.')) } finally { setBusy(false) }
  }, [job, onView, uploadId])
  const retry = async () => {
    setBusy(true); setError('')
    try {
      await retryAnalysis(uploadId, job.id, { id: crypto.randomUUID() })
      setReload(n => n + 1)
    } catch (e) { setError(apiErrorText(e, 'Coaching could not be requested again. Retry in a moment.')) } finally { setBusy(false) }
  }
  const active = ['queued', 'running'].includes(status.state)
  return <Stack spacing={0.75} role="group" aria-label="AI coaching for this clip" sx={{ px: 1, pb: 1 }}>
    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
      <Chip size="small" variant="outlined" color="warning" label="Admin beta" />
      {loaded && status.state !== 'none' && <Chip size="small" color={status.color} label={status.label} aria-label={`Clip coaching status: ${status.label}`} />}
      {status.state === 'ready' && <Button size="small" variant="contained" disabled={busy} onClick={view}>View coaching</Button>}
      {active && <Button size="small" variant="outlined" disabled>Coaching in progress…</Button>}
      {status.state === 'failed' && <Button size="small" variant="contained" color="error" disabled={busy} onClick={retry}>{busy ? 'Requesting…' : 'Retry coaching'}</Button>}
      {(status.state === 'none' || status.state === 'ready' || status.state === 'failed') &&
        <Button size="small" variant={status.state === 'none' ? 'outlined' : 'text'} disabled={busy || !loaded} onClick={onRequest}>
          {status.state === 'none' ? 'Request AI coaching for this clip' : status.state === 'ready' ? 'Request again' : 'Change details'}
        </Button>}
    </Stack>
    {active && <Typography variant="caption" color="text.secondary" sx={{ fontSize: 13 }}>One coaching run per clip at a time. This usually takes a few minutes; you can leave this page and come back.</Typography>}
    {status.state === 'failed' && <Alert severity="error" sx={{ py: 0 }}>{job.error || 'Coaching did not finish.'}</Alert>}
    {error && <Alert severity="warning" sx={{ py: 0 }}>{error}</Alert>}
  </Stack>
}
ClipCoachingStatus.propTypes = {
  uploadId: PropTypes.string.isRequired,
  highlight: PropTypes.object.isRequired,
  refreshKey: PropTypes.number,
  onRequest: PropTypes.func.isRequired,
  onView: PropTypes.func.isRequired
}
